import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRightLeft, Plus, Tag } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { EmptyState, LockedIllustration, PageHeader, Tabs } from '@/components/ui/layout';
import { Button } from '@/components/ui/primitives';
import { AdjustmentsPanel } from './AdjustmentsPanel';
import { BatchControlPanel } from './BatchControlPanel';
import { RepriceDialog } from './RepriceDialog';
import { StockTakePanel } from './StockTakePanel';

const APPROVALS_RETURN = encodeURIComponent('/stock-control?tab=approvals');
const STOCK_TAKE_RETURN = encodeURIComponent('/stock-control?tab=stock-take');
const BATCH_CONTROL_RETURN = encodeURIComponent('/stock-control?tab=batch-control');

export function StockControlPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [requestedTab, setRequestedTab] = useState<string | null>(() => params.get('tab'));
  const [countsDirty, setCountsDirty] = useState(false);
  const [takeStoreId, setTakeStoreId] = useState('');
  const [repricing, setRepricing] = useState(false);

  const tabs = [
    { id: 'approvals', label: tr('pharmacy', 'approvals'), visible: can('stock.adjust', 'stock.adjust.approve') },
    { id: 'stock-take', label: tr('pharmacy', 'stock_take_2'), visible: can('stock.take.manage', 'stock.adjust.approve') },
    { id: 'batch-control', label: tr('pharmacy', 'batch_control'), visible: can('stock.batch.manage', 'stock.reprice', 'stock.read') },
  ].filter((tab) => tab.visible);

  const active = tabs.find((tab) => tab.id === requestedTab)?.id ?? tabs[0]?.id;

  const changeTab = (id: string) => {
    if (id === active) return;
    if (countsDirty && !window.confirm(tr('pharmacy', 'you_have_counts_that_are_not_saved_leave_this'))) return;
    setRequestedTab(id);
  };

  return (
    <>
      <PageHeader
        title={tr('pharmacy', 'stock_control')}
        description={tr('pharmacy', 'approve_adjustments_count_the_shelves_and')}
        actions={
          <>
            {active === 'approvals' && can('stock.adjust') && (
              <>
                <Button variant="outline" onClick={() => navigate(`/stock-control/transfer?returnTo=${APPROVALS_RETURN}`)}>
                  <ArrowRightLeft className="h-4 w-4" /> {tr('pharmacy', 'transfer_stock')}
                </Button>
                <Button onClick={() => navigate(`/stock-control/adjust?returnTo=${APPROVALS_RETURN}`)}>
                  <Plus className="h-4 w-4" /> {tr('pharmacy', 'new_adjustment')}
                </Button>
              </>
            )}
            {active === 'stock-take' && can('stock.take.manage') && (
              <Button
                onClick={() =>
                  navigate(`/stock-control/count-sheets/new?returnTo=${STOCK_TAKE_RETURN}${takeStoreId ? `&storeId=${takeStoreId}` : ''}`)
                }
              >
                <Plus className="h-4 w-4" /> {tr('pharmacy', 'new_count_sheet')}
              </Button>
            )}
            {active === 'batch-control' && can('stock.reprice') && (
              <Button variant="outline" onClick={() => setRepricing(true)}>
                <Tag className="h-4 w-4" /> {tr('pharmacy', 'reprice')}
              </Button>
            )}
            {active === 'batch-control' && can('stock.batch.manage') && (
              <Button onClick={() => navigate(`/stock-control/batch-status?returnTo=${BATCH_CONTROL_RETURN}`)}>
                {tr('pharmacy', 'quarantine_recall')}
              </Button>
            )}
          </>
        }
      />

      {active === undefined ? (
        <EmptyState
          illustration={<LockedIllustration />}
          title={tr('pharmacy', 'nothing_here_for_your_role')}
          description={tr('pharmacy', 'stock_control_needs_permission_to_adjust_or')}
        />
      ) : (
        <>
          <Tabs active={active} onChange={changeTab} tabs={tabs.map(({ id, label }) => ({ id, label }))} />
          {active === 'approvals' && <AdjustmentsPanel />}
          {active === 'stock-take' && <StockTakePanel storeId={takeStoreId} onStoreChange={setTakeStoreId} onDirtyChange={setCountsDirty} />}
          {active === 'batch-control' && <BatchControlPanel />}
        </>
      )}

      {repricing && <RepriceDialog onClose={() => setRepricing(false)} />}
    </>
  );
}
